import React, { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toastWarning } from 'utils/toast';
import { encryptURL } from 'utils/functions';
import { AuthContext } from 'contexts/AuthContext';
import { DropsContext } from 'contexts/DropsContext';
import { DocumentosContext } from 'contexts/DocumentosContext';
import { EstoqueContext } from 'contexts/EstoqueContext';
import { GridContext } from 'contexts/GridContext';
import { useModal } from 'components/Modals';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import getEstoqueFilterFields from './filterFields';
import columnOverrides from './columnOverrides';

const Estoque = () => {
  const rotina = 'Estoque';
  const titulo = 'Movimentações de Estoque';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { estoque, getEstoque, getLoading } = useContext(EstoqueContext);
  const { postDocumentos, postArmazenagem } = useContext(DocumentosContext);
  const filterFields = useMemo(() => getEstoqueFilterFields(drops), [drops]);

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();
    closeModal();
    getEstoque({ ...props, rotina, filter });
  };

  const toolbarActions = [
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getValues(), order: estoque?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getValues(), order: estoque?.order }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={estoque?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
    {
      name: 'Gerar Inventário',
      icon: 'inventory_2',
      action: () => navigate('/app/Estoque/Inventario'),
      show: !user?.permissoes?.block_estoque_inventario,
    },
    {
      name: 'Gerar Transferência',
      icon: 'sync',
      action: () => navigate('/app/Estoque/Transferencia'),
      show: !user?.permissoes?.block_estoque_transferencia,
    },
    {
      name: 'Gerar Movimentação',
      icon: 'subdirectory_arrow_right',
      action: () => navigate('/app/Estoque/Movimentacao'),
      show: !user?.permissoes?.block_estoque_movimentacao,
    },
    {
      name: 'Registrar Documentos',
      icon: 'send',
      action: (selected) => {
        const documentos = selected?.filter((f) =>
          Boolean(f?.status !== 'CONCLUIDO')
        );
        if (!Boolean(documentos?.length)) {
          return toastWarning('Selecione apenas documentos não concluídos');
        }
        postDocumentos({
          data: selected?.map((s) => ({
            Documento: { id: s?.id, conferido: 'SIM' },
          })),
          cb: loadGrid,
        });
      },
    },
    {
      name: 'Liberar Expedição',
      icon: 'local_shipping',
      action: (arr) => {
        const sts_exp = findOnArray(13, drops?.StatusDocumento, 'status');
        if (!Boolean(arr?.length))
          return toastWarning('Selecione ao menos uma movimentação');
        if (arr?.some((a) => a?.status !== sts_exp))
          return toastWarning(`Selecione apenas movimentações ${sts_exp}`);
        if (arr?.some((a) => a?.operacao !== 'SAIDA'))
          return toastWarning('Selecione apenas movimentações de saída');
        if (
          ['WEMOV', 'INCUBADORA_E']?.some((a) => user?.integracao?.includes(a))
        ) {
          const data = arr?.map((d) => ({
            Registros: [{ documento_id: d?.id, status_id: 13 }],
          }));
          postDocumentos({ data, cb: loadGrid });
        } else {
          const hash = encryptURL(arr?.map((d) => d?.id));
          navigate(`/app/Estoque/Expedicao/${hash}`);
        }
      },
    },
    {
      name: 'Armazenagem',
      icon: 'warehouse',
      action: (arr) => {
        if (!Boolean(arr?.length))
          return toastWarning('Selecione ao menos uma movimentação');
        if (arr?.some((a) => a?.status !== 'PENDENTE'))
          return toastWarning('Selecione apenas movimentações pendentes');
        if (arr?.some((a) => a?.operacao !== 'ENTRADA'))
          return toastWarning('Selecione apenas movimentações de entrada');
        postArmazenagem({
          data: { ids: arr?.map((m) => m?.id) },
          cb: loadGrid,
        });
      },
    },
  ];

  const rowActions = [
    {
      name: 'Aprovação necessária',
      icon: 'priority_high',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'PENDENTE',
    },
    {
      name: 'Aprovado',
      icon: 'check',
      color: 'primary.main',
      show: ({ row }) => row?.aprovacao === 'SIM',
    },
    {
      name: ' Reprovado',
      icon: 'close',
      color: 'secondary.main',
      show: ({ row }) => row?.aprovacao === 'NAO',
    },
    {
      name: 'Detalhes',
      icon: 'visibility',
      action: ({ row }) => navigate(`/app/Documento/${row?.id}`),
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} titulo={titulo} colunas={estoque?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={estoque}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={getLoading}
          loadGrid={loadGrid}
        />
      </Card>
    </Container>
  );
};

export default Estoque;
